import { ArrowDownTrayIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useIteroScanMeshes } from '../../../../../hooks/useIteroScanAssets';
import { downloadBlob } from '../../../../../utils/downloadBlob';
import { NEUTRAL_BUTTON_CLASS } from '../../details/CredsActionButtons';
import { assetFilename } from '../assetDownload';
import { browseErrorText } from '../browseErrorText';
import { BrowserOverlay } from '../BrowserOverlay';
import { LINK_BUTTON_CLASS } from '../browserStyles';
import { useIteroBrowserScope } from '../iteroBrowserScope';
import { MyIteroLink } from '../MyIteroLink';
import { ScanMeshViewer } from './ScanMeshViewer';
import { scanViewerView, type ScanViewerView } from './scanPreviewState';
import type { ScanMeshSet } from './scanZip';
import { ViewerMessage } from './ViewerMessage';

const DOWNLOAD_LABEL = 'Download 3D files';
const PLY_ONLY_TITLE =
    "This scan was exported in color (PLY), which the full viewer doesn't open yet.";

interface ViewerBodyProps {
    view: ScanViewerView<ScanMeshSet>;
    meshesKey: number;
    viewerUrl: string | null;
    onRetry: () => void;
    onDownload?: () => void;
}

function ViewerBody({ view, meshesKey, viewerUrl, onRetry, onDownload }: ViewerBodyProps) {
    switch (view.status) {
        case 'downloading':
            return <ViewerMessage title="Downloading 3D files…" busy percent={view.percent} />;
        case 'unzipping':
            return <ViewerMessage title="Opening the 3D files…" busy />;
        case 'generating':
            return (
                <ViewerMessage
                    title="Preparing the 3D files. Try again in about a minute."
                    actionLabel="Try again"
                    onAction={onRetry}
                />
            );
        case 'empty':
            return <ViewerMessage title="No 3D files for this order" />;
        case 'no-meshes':
            return (
                <ViewerMessage
                    title="No 3D models in the 3D files."
                    detail="Download the 3D files to see what the export contains."
                    actionLabel={DOWNLOAD_LABEL}
                    actionIcon={ArrowDownTrayIcon}
                    onAction={onDownload}
                />
            );
        case 'ply-only':
            return (
                <ViewerMessage
                    title={PLY_ONLY_TITLE}
                    detail="Open it in myiTero or download the 3D files."
                    actionLabel={DOWNLOAD_LABEL}
                    actionIcon={ArrowDownTrayIcon}
                    onAction={onDownload}
                >
                    <MyIteroLink viewerUrl={viewerUrl} />
                </ViewerMessage>
            );
        case 'error':
            return <ViewerMessage title={view.message} actionLabel="Retry" onAction={onRetry} />;
        case 'ready':
            // Keyed by fetch: the viewer caches each file's URL, and a refetch revokes the old ones.
            return (
                <div className="absolute inset-0">
                    <ScanMeshViewer key={meshesKey} meshes={view.meshes} />
                </div>
            );
    }
}

interface ScanViewerOverlayProps {
    rxId: string;
    orderCode: string;
    viewerUrl: string | null;
    onClose: () => void;
}

export function ScanViewerOverlay({ rxId, orderCode, viewerUrl, onClose }: ScanViewerOverlayProps) {
    const scope = useIteroBrowserScope();
    const { query, phase } = useIteroScanMeshes(scope, rxId);
    const view = scanViewerView({
        result: query.data,
        isFetching: query.isFetching,
        errorMessage: query.isError
            ? browseErrorText(query.error, "Couldn't load the 3D files")
            : null,
        phase
    });
    const archive = query.data?.kind === 'ready' ? query.data.archive : null;
    const download = archive
        ? () => downloadBlob(archive.blob, assetFilename(archive, orderCode, 'IDE'))
        : undefined;

    return (
        <BrowserOverlay
            label={`3D scan for order ${orderCode}`}
            onClose={onClose}
            className="bg-white"
        >
            <div className="flex h-[56px] shrink-0 items-center gap-4 border-b border-gray-200 px-5">
                <button
                    type="button"
                    onClick={onClose}
                    className={`${LINK_BUTTON_CLASS} inline-flex items-center gap-1`}
                >
                    <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Back to details
                </button>
                <h3 className="min-w-0 flex-1 truncate text-[15px] font-semibold text-gray-800">
                    3D scan · {orderCode}
                </h3>
                {view.status === 'ready' && download && (
                    <button type="button" onClick={download} className={NEUTRAL_BUTTON_CLASS}>
                        <ArrowDownTrayIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        {DOWNLOAD_LABEL}
                    </button>
                )}
            </div>
            <div className="relative flex-1 min-h-0 bg-gray-50">
                <ViewerBody
                    view={view}
                    meshesKey={query.dataUpdatedAt}
                    viewerUrl={viewerUrl}
                    onRetry={() => void query.refetch()}
                    onDownload={download}
                />
            </div>
        </BrowserOverlay>
    );
}
